import { useState, useEffect } from 'react';
import {
  Monitor, ChevronRight, RefreshCw, AlertCircle, CheckCircle2, SkipForward
} from 'lucide-react';
import useSiteSetupStore from '../../../stores/siteSetupStore';
import fetchWithTenant from '../../../utils/fetchWithTenant';

const MONITOR_DEFAULT_TYPES = new Set([
  'CAMERA', 'NVR', 'SWITCH', 'ACCESS_CONTROL',
  'ACS_PANEL', 'ACS_READER', 'INTERCOM', 'ROUTER'
]);

const PROFILE_OPTIONS = [
  { value: 'PING', label: 'Ping (ICMP)' },
  { value: 'HTTP_CHECK', label: 'HTTP check' },
  { value: 'RTSP_CHECK', label: 'RTSP check' },
  { value: 'API_HEALTH', label: 'API health' },
  { value: 'SNMP_POLL', label: 'SNMP poll' },
  { value: 'WMI', label: 'WMI (Windows)' },
];

const POLL_OPTIONS = [
  { value: 30, label: '30 seconds' },
  { value: 60, label: '1 minute' },
  { value: 120, label: '2 minutes' },
  { value: 300, label: '5 minutes' },
  { value: 600, label: '10 minutes' },
];

const ALERT_OPTIONS = [
  { value: 60, label: '1 minute' },
  { value: 120, label: '2 minutes' },
  { value: 300, label: '5 minutes' },
  { value: 600, label: '10 minutes' },
  { value: 900, label: '15 minutes' },
];

const MonitoringStep = ({ onNext }) => {
  const { markStepComplete } = useSiteSetupStore();

  const [mode, setMode] = useState(null); // null | 'setup' | 'skip'
  const [devices, setDevices] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [profile, setProfile] = useState('PING');
  const [pollInterval, setPollInterval] = useState(60);
  const [alertThreshold, setAlertThreshold] = useState(300);

  useEffect(() => {
    if (mode === 'setup') loadDevices();
    // eslint-disable-next-line
  }, [mode]);

  const loadDevices = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithTenant('/api/md/devices');
      if (!res.ok) throw new Error('Failed to load devices');
      const data = await res.json();
      const list = (data.devices || data || []).filter(d => !d.isMonitored);
      setDevices(list);
      const defaults = new Set(
        list.filter(d => MONITOR_DEFAULT_TYPES.has((d.deviceType || '').toUpperCase()))
            .map(d => d.id)
      );
      setSelected(defaults);
    } catch (err) {
      console.error('[Monitoring] load failed:', err);
      setError(err.message || 'Failed to load devices');
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = () => {
    if (selected.size === devices.length) setSelected(new Set());
    else setSelected(new Set(devices.map(d => d.id)));
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const handleSkip = async () => {
    await markStepComplete('monitoring', { skipped: true });
    onNext();
  };

  const handlePromote = async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetchWithTenant('/api/md/monitored/promote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deviceIds: Array.from(selected),
          defaults: {
            monitoringProfile: profile,
            pollIntervalSeconds: pollInterval,
            alertThresholdSeconds: alertThreshold,
          },
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || 'Failed to promote devices');
      }
      const data = await res.json();
      setResult({
        promoted: data.promoted || 0,
        skipped: data.skipped || 0,
        errors: data.errors || [],
      });
      await markStepComplete('monitoring', {
        promoted: data.promoted || 0,
        skipped: data.skipped || 0,
        profile, pollInterval, alertThreshold,
      });
    } catch (err) {
      console.error('[Monitoring] promote failed:', err);
      setError(err.message || 'Failed to promote devices');
    } finally {
      setSubmitting(false);
    }
  };

  // Initial branching screen
  if (mode === null) {
    return (
      <div className="site-setup-step monitoring-step">
        <h3>Setup Monitoring</h3>
        <p className="step-description">
          Continuously monitor uptime and health of the devices we just discovered, or skip and configure later.
        </p>
        <div className="monitoring-choice-grid">
          <button className="monitoring-choice-card primary" onClick={() => setMode('setup')}>
            <Monitor size={28} />
            <strong>Set up monitoring now</strong>
            <span>Pick devices, profile, and poll interval. We'll start watching them right away.</span>
          </button>
          <button className="monitoring-choice-card" onClick={handleSkip}>
            <SkipForward size={28} />
            <strong>Skip for now</strong>
            <span>You can promote devices to monitoring at any time from the Monitoring page.</span>
          </button>
        </div>
      </div>
    );
  }

  // Setup screen with results state
  if (result) {
    return (
      <div className="site-setup-step monitoring-step">
        <h3>Monitoring Enabled</h3>
        <div className="scan-summary success">
          <CheckCircle2 size={20} />
          <div>
            <strong>{result.promoted} device{result.promoted === 1 ? '' : 's'} now being monitored.</strong>
            {result.skipped > 0 && <p>{result.skipped} skipped (already monitored).</p>}
            {result.errors.length > 0 && <p>{result.errors.length} error{result.errors.length === 1 ? '' : 's'}.</p>}
          </div>
        </div>
        <div className="step-actions">
          <button className="site-setup-btn primary" onClick={onNext}>
            Continue <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="site-setup-step monitoring-step">
      <h3>Setup Monitoring</h3>
      <p className="step-description">
        Select devices to monitor. We've pre-selected likely candidates (cameras, NVRs, switches, access control).
      </p>

      {error && (
        <div className="pairing-code-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="setup-form-section">
        <div className="setup-form-row three-col">
          <div className="setup-form-field">
            <label>Monitoring profile</label>
            <select value={profile} onChange={(e) => setProfile(e.target.value)}>
              {PROFILE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="setup-form-field">
            <label>Poll interval</label>
            <select value={pollInterval} onChange={(e) => setPollInterval(Number(e.target.value))}>
              {POLL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="setup-form-field">
            <label>Alert after</label>
            <select value={alertThreshold} onChange={(e) => setAlertThreshold(Number(e.target.value))}>
              {ALERT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="verify-loading">
          <RefreshCw size={20} className="spin" />
          <p>Loading devices...</p>
        </div>
      ) : devices.length === 0 ? (
        <div className="scan-summary empty">
          <AlertCircle size={20} />
          <div>
            <strong>No unmonitored devices available.</strong>
            <p>Either nothing was discovered or all devices are already monitored.</p>
          </div>
        </div>
      ) : (
        <div className="device-results-table-wrap">
          <table className="device-results-table selectable">
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <input
                    type="checkbox"
                    checked={selected.size === devices.length && devices.length > 0}
                    onChange={toggleAll}
                  />
                </th>
                <th>IP</th>
                <th>Hostname</th>
                <th>Type</th>
                <th>Manufacturer</th>
              </tr>
            </thead>
            <tbody>
              {devices.map(d => (
                <tr key={d.id} className={selected.has(d.id) ? 'selected' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(d.id)}
                      onChange={() => toggleOne(d.id)}
                    />
                  </td>
                  <td>{d.ipAddress || '—'}</td>
                  <td>{d.hostname || '—'}</td>
                  <td>{d.deviceType || 'UNKNOWN'}</td>
                  <td>{d.manufacturer || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="step-actions">
        <button className="site-setup-btn ghost" onClick={handleSkip}>
          <SkipForward size={14} /> Skip for Now
        </button>
        <button
          className="site-setup-btn primary"
          onClick={handlePromote}
          disabled={selected.size === 0 || submitting}
        >
          {submitting ? 'Enabling...' : `Monitor ${selected.size} Selected`} <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default MonitoringStep;
